/**
 * Polymorphic Position System
 * Supports multiple positioning modes for flexible layer placement
 */

// Alignment-based positioning (9-point grid)
export type Alignment =
  | 'TopLeft'
  | 'TopCenter'
  | 'TopRight'
  | 'CenterLeft'
  | 'Center'
  | 'CenterRight'
  | 'BottomLeft'
  | 'BottomCenter'
  | 'BottomRight';

// Horizontal constraint types for constraint-based positioning
export type HorizontalConstraint =
  | { type: 'left'; offset: number }
  | { type: 'right'; offset: number }
  | { type: 'center'; offset?: number }
  | { type: 'stretch'; left: number; right: number }
  | { type: 'scale' };

// Vertical constraint types for constraint-based positioning
export type VerticalConstraint =
  | { type: 'top'; offset: number }
  | { type: 'bottom'; offset: number }
  | { type: 'center'; offset?: number }
  | { type: 'stretch'; top: number; bottom: number }
  | { type: 'scale' };

// Polymorphic position - supports three positioning modes
export type LayerPosition =
  | { mode: 'absolute'; x: number; y: number }
  | { mode: 'alignment'; alignment: Alignment; offsetX?: number; offsetY?: number }
  | { mode: 'constraints'; horizontal: HorizontalConstraint; vertical: VerticalConstraint };

// Default position factory functions
export const createAbsolutePosition = (x: number, y: number): LayerPosition => ({
  mode: 'absolute',
  x,
  y,
});

export const createAlignmentPosition = (
  alignment: Alignment,
  offsetX = 0,
  offsetY = 0
): LayerPosition => ({
  mode: 'alignment',
  alignment,
  offsetX,
  offsetY,
});

export const createConstraintPosition = (
  horizontal: HorizontalConstraint,
  vertical: VerticalConstraint
): LayerPosition => ({
  mode: 'constraints',
  horizontal,
  vertical,
});

// Type guards
export const isAbsolutePosition = (
  pos: LayerPosition
): pos is { mode: 'absolute'; x: number; y: number } => pos.mode === 'absolute';

export const isAlignmentPosition = (
  pos: LayerPosition
): pos is { mode: 'alignment'; alignment: Alignment; offsetX?: number; offsetY?: number } =>
  pos.mode === 'alignment';

export const isConstraintPosition = (
  pos: LayerPosition
): pos is { mode: 'constraints'; horizontal: HorizontalConstraint; vertical: VerticalConstraint } =>
  pos.mode === 'constraints';
